import { IGroupedSubTopic, ISubTopic } from '../store/SubTopicSlice';

/**
 * Check if a subtopic has resources available
 */
export const checkResources = (
  subTopic: IGroupedSubTopic | ISubTopic
): boolean => !!(subTopic.resources && subTopic.resources.length);

/**
 * Check if a subtopic has videos available
 */
export const checkVideos = (subTopic: IGroupedSubTopic | ISubTopic): boolean =>
  !!(subTopic.videoLinks && subTopic.videoLinks.length);

/**
 * Check if a subtopic has quizzes available
 */
export const checkQuizzes = (subTopic: IGroupedSubTopic | ISubTopic): boolean =>
  !!(subTopic.quizCount && subTopic.quizCount > 0);

/**
 * Check if a subtopic has practice questions available
 */
export const checkPracticeQuestions = (
  subTopic: IGroupedSubTopic | ISubTopic
): boolean => !!(subTopic.questionsCount && subTopic.questionsCount > 8);

/**
 * Interface for progress completion flags
 */
export interface IProgressCompletionFlags {
  videoCompletion?: boolean;
  practiceQuestionCompletion?: boolean;
  quizCompletion?: boolean;
  resourceCompletion?: number;
}

/**
 * Determines which completion flags should be set based on available parts in a subtopic
 * If a part is not available, it will be marked as completed (true) to indicate it's not applicable
 *
 * @param subTopic - The subtopic to check
 * @param currentFlags - Current completion flags (optional)
 * @returns Object with completion flags for all parts
 */
export const getProgressCompletionFlags = (
  subTopic: IGroupedSubTopic | ISubTopic,
  currentFlags?: IProgressCompletionFlags
): IProgressCompletionFlags => {
  const hasResources = checkResources(subTopic);
  const hasVideos = checkVideos(subTopic);
  const hasQuizzes = checkQuizzes(subTopic);
  const hasPracticeQuestions = checkPracticeQuestions(subTopic);

  return {
    // If videos are not available, mark as completed (not applicable)
    videoCompletion: hasVideos ? currentFlags?.videoCompletion : true,

    // If practice questions are not available, mark as completed (not applicable)
    practiceQuestionCompletion: hasPracticeQuestions
      ? currentFlags?.practiceQuestionCompletion
      : true,

    // If quizzes are not available, mark as completed (not applicable)
    quizCompletion: hasQuizzes ? currentFlags?.quizCompletion : true,

    // If resources are not available, mark as completed (not applicable)
    resourceCompletion: hasResources ? currentFlags?.resourceCompletion : 34,
  };
};

/**
 * Creates a progress payload with automatic completion flags for unavailable parts
 *
 * @param subTopicId - The subtopic ID
 * @param subTopic - The subtopic data
 * @param currentFlags - Current completion flags (optional)
 * @returns Progress payload with all necessary completion flags
 */
export const createProgressPayload = (
  subTopicId: string,
  subTopic: IGroupedSubTopic | ISubTopic,
  currentFlags?: IProgressCompletionFlags
): {
  subTopicId: string;
  videoCompletion?: boolean;
  practiceQuestionCompletion?: boolean;
  quizCompletion?: boolean;
  resourceCompletion?: number;
} => {
  const completionFlags = getProgressCompletionFlags(subTopic, currentFlags);

  return {
    subTopicId,
    ...completionFlags,
  };
};
